import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useHeroCarousel } from "@/hooks/useHeroCarousel";
import type { HeroCarouselData } from "@/lib/hero-carousel-query";
import { sortSlides, type HeroSlide } from "@/lib/hero-carousel";
import { imageSrcSet, sizedImageUrl } from "@/lib/image-url";

// The banner never renders wider than ~640 CSS px on desktop (half column) and
// full width on phones, so we cap the variants we offer and shave quality a
// little — the visual result is identical but the bytes are far smaller.
const HERO_WIDTHS = [320, 480, 640, 768, 960, 1200] as const;
const HERO_WIDTHS_DEFERRED = [320, 480, 640, 768] as const;
const HERO_QUALITY = 68;
export const HERO_SIZES = "(min-width: 1280px) 640px, (min-width: 900px) 50vw, 100vw";

export function heroPreloadFor(slides: HeroSlide[] | undefined) {
  const first = sortSlides((slides ?? []).filter((s) => s.enabled && s.image_url))[0];
  if (!first) return null;
  return {
    href: sizedImageUrl(first.image_url, 768, HERO_QUALITY),
    imagesrcset: imageSrcSet(first.image_url, HERO_WIDTHS, HERO_QUALITY),
    imagesizes: HERO_SIZES,
  };
}

export function HeroSlideshow({ initial }: { initial?: HeroCarouselData } = {}) {
  const [i, setI] = useState(0);
  const carousel = useHeroCarousel(initial);

  const SLIDES: HeroSlide[] = useMemo(() => {
    const managed = (carousel.data?.slides ?? []).filter((s) => s.enabled && s.image_url);
    return sortSlides(managed);
  }, [carousel.data?.slides]);

  const settings = carousel.data?.settings;
  const len = SLIDES.length;
  const resolved = carousel.data != null;

  useEffect(() => {
    if (i >= len) setI(0);
  }, [i, len]);


  // Only the first slide loads with the page; the rest wait until the browser
  // is idle so the hero never competes with critical work.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setDeferredReady(true), { timeout: 3000 });
      return () => (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setDeferredReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!deferredReady || len < 2 || settings?.autoplay === false) return;
    const ms = settings?.autoplay_ms ?? 6500;
    const id = setInterval(() => setI((n) => (n + 1) % len), ms);
    return () => clearInterval(id);
  }, [len, settings?.autoplay, settings?.autoplay_ms]);

  // Space is reserved while slides are still loading so nothing below the
  // banner jumps once it appears. Only a confirmed-empty carousel collapses.
  if (!len) {
    if (resolved) return null;
    return <div aria-hidden className="aspect-video w-full bg-background" />;
  }


  return (
    <section
      aria-roledescription="carousel"
      aria-label="Featured TruWe formulas"
      className="relative isolate w-full overflow-hidden bg-background"
    >
      <div className="relative aspect-video w-full">
        {SLIDES.map((s, idx) => (
          <Link
            key={s.id}
            to={s.link_path as never}
            className="absolute inset-0 transition-opacity duration-[700ms] ease-out"
            style={{ opacity: idx === i ? 1 : 0, pointerEvents: idx === i ? "auto" : "none" }}
            aria-hidden={idx !== i}
            aria-label={s.alt || "View featured TruWe formula"}
            tabIndex={idx === i ? undefined : -1}
          >
            {(idx === 0 || deferredReady) && (
              <img
                src={sizedImageUrl(s.image_url, idx === 0 ? 768 : 640, HERO_QUALITY)}
                srcSet={imageSrcSet(
                  s.image_url,
                  idx === 0 ? HERO_WIDTHS : HERO_WIDTHS_DEFERRED,
                  HERO_QUALITY,
                )}
                sizes={HERO_SIZES}
                alt={s.alt}
                loading={idx === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={idx === 0 ? "high" : "low"}
                width={1536}
                height={864}
                className="h-full w-full object-contain object-center"
              />
            )}


          </Link>
        ))}
      </div>


    </section>
  );
}
